<template>
  <section class="app-table-wrap customers-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">{{ text.table.headers.index }}</th>
            <th>
              <button
                type="button"
                class="customers-table-sort-btn"
                :title="text.actions.table.sortByCustomer"
                @click="emitSort('name')"
              >
                <span>{{ text.table.headers.customer }}</span>
                <i class="fa" :class="sortIconClass('name')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.headers.mainContact }}</th>
            <th>{{ text.table.headers.email }}</th>
            <th v-if="canViewSales" class="app-text-right">
              <button
                type="button"
                class="customers-table-sort-btn customers-table-sort-btn--right"
                :title="text.actions.table.sortBySales"
                @click="emitSort('sales')"
              >
                <span>{{ text.table.headers.sales }}</span>
                <i class="fa" :class="sortIconClass('sales')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="canViewTotal" class="app-text-right">
              <button
                type="button"
                class="customers-table-sort-btn customers-table-sort-btn--right"
                :title="text.actions.table.sortByIncomeForecast"
                @click="emitSort('income_forecast')"
              >
                <span>{{ text.table.headers.incomeForecast }}</span>
                <i class="fa" :class="sortIconClass('income_forecast')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="canViewTotal" class="app-text-right">
              <button
                type="button"
                class="customers-table-sort-btn customers-table-sort-btn--right"
                :title="text.actions.table.sortByIncomeBilled"
                @click="emitSort('income_billed')"
              >
                <span>{{ text.table.headers.incomeBilled }}</span>
                <i class="fa" :class="sortIconClass('income_billed')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.headers.lastInvoice }}</th>
            <th>{{ text.table.headers.status }}</th>
            <th v-if="showActionsColumn"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="visibleColumnsCount" class="app-table-empty">{{ text.table.loading }}</td>
          </tr>

          <tr v-else-if="rows.length === 0">
            <td :colspan="visibleColumnsCount" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in rows" :key="row.id">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td class="app-strong">
              <a
                v-if="canViewDetail"
                :href="resolveCustomerHref(row)"
                class="customers-table-link"
                :title="text.actions.table.goCustomerDetail"
                @click="navigateToCustomerDetail(row, $event)"
              >
                {{ row.name || '' }}
              </a>
              <span v-else>{{ row.name || '' }}</span>
            </td>
            <td>
              <span v-if="row.main_contact_name">{{ row.main_contact_name }}</span>
              <span v-else-if="row.main_contact && row.main_contact.name">{{ row.main_contact.name }}</span>
              <span v-else class="app-muted"></span>
            </td>
            <td>
              <span v-if="row.email">{{ row.email }}</span>
              <span v-else class="app-muted"></span>
            </td>
            <td v-if="canViewSales" class="app-text-right">
              <a
                v-if="canViewDetail && Number(row?.sales || 0) > 0"
                :href="resolveCustomerSalesHref(row)"
                class="customers-table-link"
                :title="text.actions.table.goCustomerSales"
                @click="navigateToCustomerSalesTab(row, $event)"
              >
                {{ row.sales || 0 }}
              </a>
              <span v-else>{{ row.sales || 0 }}</span>
            </td>
            <td v-if="canViewTotal" class="app-text-right app-strong">{{ formatCurrency(row.income_forecast) }}</td>
            <td v-if="canViewTotal" class="app-text-right app-strong">{{ formatCurrency(row.income_billed) }}</td>
            <td>
              <a
                v-if="canOpenInvoiceDetail(row)"
                :href="resolveInvoiceHref(row)"
                class="customers-table-link"
                :title="text.actions.table.goInvoiceDetail"
                @click="navigateToInvoiceDetail(row, $event)"
              >
                {{ formatLastInvoice(row) }}
              </a>
              <span v-else>{{ formatLastInvoice(row) }}</span>
            </td>
            <td>
              <span class="app-status-pill" :class="row.active ? 'app-status-active' : 'app-status-inactive'">
                {{ row.active ? text.table.status.active : text.table.status.inactive }}
              </span>
            </td>

            <td v-if="showActionsColumn" class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  v-if="canViewDetail"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="text.actions.table.viewDetail"
                  @click="navigateToCustomerDetail(row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  v-if="canEdit"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--edit"
                  :title="text.actions.table.editCustomer"
                  @click="emitEdit(row)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>

                <div v-if="canEdit || canDelete" class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="text.actions.common.moreActions"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button
          v-if="canEdit"
          type="button"
          @click="emitAction('toggle-status', openRow)"
        >
          {{ openRow.active ? text.actions.common.disable : text.actions.common.activate }}
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="app-table-actions-menu-btn--danger"
          @click="emitAction('delete', openRow)"
        >
          {{ text.actions.common.delete }}
        </button>
      </div>
    </teleport>

  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { ensureValidPage, normalizePaginationPayload } from '@/composables/shared/usePagination';
import { formatCurrency as formatCurrencyUtil } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
} from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomersTable',
  components: {
    AppPagination,
  },
  emits: ['edit-customer', 'delete-customer', 'toggle-customer-status', 'page-change', 'sort-change'],
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      }),
    },
    emptyText: {
      type: String,
      default: text.table.empty,
    },
    capabilities: {
      type: Object,
      default: () => ({
        canViewDetail: true,
        canDelete: true,
        canEdit: true,
        canViewSales: true,
        canViewTotal: true,
      }),
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
  },
  data() {
    return {
      text,
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
    };
  },
  computed: {
    normalizedPagination() {
      return normalizePaginationPayload(this.pagination, {
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      });
    },
    normalizedCapabilities() {
      const caps = this.capabilities || {};

      return {
        canViewDetail: Boolean(caps.canViewDetail),
        canDelete: Boolean(caps.canDelete),
        canEdit: Boolean(caps.canEdit),
        canViewSales: Boolean(caps.canViewSales),
        canViewTotal: Boolean(caps.canViewTotal),
      };
    },
    canViewDetail() {
      return this.normalizedCapabilities.canViewDetail;
    },
    canDelete() {
      return this.normalizedCapabilities.canDelete;
    },
    canEdit() {
      return this.normalizedCapabilities.canEdit;
    },
    canViewSales() {
      return this.normalizedCapabilities.canViewSales;
    },
    canViewTotal() {
      return this.normalizedCapabilities.canViewTotal;
    },
    showActionsColumn() {
      return this.canViewDetail || this.canDelete || this.canEdit;
    },
    visibleColumnsCount() {
      let count = 6; // #, cliente, contacto, email, ultima factura, estado

      if (this.canViewSales) {
        count += 1;
      }

      if (this.canViewTotal) {
        count += 2;
      }

      if (this.showActionsColumn) {
        count += 1;
      }

      return count;
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.rows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeActionsMenu);
    window.addEventListener('scroll', this.closeActionsMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeActionsMenu);
    window.removeEventListener('scroll', this.closeActionsMenu, true);
  },
  methods: {
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    emitSort(field) {
      const nextSort = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!nextSort) {
        return;
      }

      this.$emit('sort-change', nextSort);
    },
    formatCurrency(value) {
      return formatCurrencyUtil(value);
    },
    formatLastInvoice(row) {
      const serial = String(row?.last_invoice?.serial || '').trim();
      const folio = String(row?.last_invoice?.folio || '').trim();

      return `${serial}${folio}`;
    },
    resolveInvoiceId(row) {
      return Number(row?.last_invoice?.id || 0);
    },
    canOpenInvoiceDetail(row) {
      return this.resolveInvoiceId(row) > 0;
    },
    resolveInvoiceHref(row) {
      return resolveRouteHref(this.$router, 'invoiceDetail', 'invoiceId', this.resolveInvoiceId(row));
    },
    isModifiedClick(event) {
      return Boolean(
        event
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      );
    },
    navigateToInvoiceDetail(row, event = null) {
      if (!this.canOpenInvoiceDetail(row)) {
        return;
      }

      if (this.isModifiedClick(event)) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      const invoiceId = this.resolveInvoiceId(row);

      try {
        sessionStorage.setItem('app.breadcrumb.invoice-detail', JSON.stringify({
          contextId: String(invoiceId),
          source: 'customer-detail',
          customerId: Number(row?.id || 0) || 0,
          customerName: String(row?.name || '').trim(),
          saleId: 0,
          saleName: '',
        }));
      } catch (error) {
        // Keep navigation flow even if session storage is unavailable.
      }

      const lastInvoice = row?.last_invoice && typeof row.last_invoice === 'object'
        ? row.last_invoice
        : {};

      this.$router.push({
        name: 'invoiceDetail',
        params: { invoiceId: String(invoiceId) },
        state: {
          invoiceSnapshot: {
            id: invoiceId,
            serial: String(lastInvoice?.serial || '').trim(),
            folio: String(lastInvoice?.folio || '').trim(),
            uuid: String(lastInvoice?.uuid || '').trim(),
            customer_id: Number(row?.id || 0) || 0,
            customer_name: String(row?.name || '').trim(),
            sale_id: Number(lastInvoice?.sale_id || 0) || 0,
            sale_code: String(lastInvoice?.sale_code || '').trim(),
            sale_name: String(lastInvoice?.sale_name || '').trim(),
            due_date: String(lastInvoice?.due_date || lastInvoice?.date || '').trim(),
            balance: Number(lastInvoice?.balance || 0) || 0,
            status_key: String(lastInvoice?.status_key || '').trim(),
            status: String(lastInvoice?.status || '').trim(),
            payment_status: String(lastInvoice?.payment_status || '').trim(),
            payment_id: Number(lastInvoice?.payment_id || lastInvoice?.payment?.id || 0) || 0,
            payment_receipt_id: Number(lastInvoice?.payment_receipt_id || 0) || 0,
            payment_receipt_code: String(lastInvoice?.payment_receipt_code || lastInvoice?.payment?.code || '').trim(),
            payment_reference: String(lastInvoice?.payment_reference || lastInvoice?.payment?.reference || '').trim(),
            has_received_proof: Boolean(lastInvoice?.has_received_proof),
            last_sent_at: String(lastInvoice?.last_sent_at || '').trim(),
          },
        },
      }).catch(() => {});
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.id);
    },
    resolveCustomerSalesHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.id, { tab: 'sales' });
    },
    navigateToCustomerDetail(row, event = null) {
      if (!this.canViewDetail) {
        return;
      }

      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.id, event);
    },
    navigateToCustomerSalesTab(row, event = null) {
      if (!this.canViewDetail) {
        return;
      }

      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.id, event, { tab: 'sales' });
    },
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 20);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    emitEdit(row) {
      this.$emit('edit-customer', row);
    },
    emitDelete(row) {
      this.$emit('delete-customer', row);
    },
    emitToggleStatus(row) {
      this.$emit('toggle-customer-status', row);
    },
    toggleActionsMenu(rowId, event) {
      if (!event?.currentTarget) {
        return;
      }

      const nextId = resolveToggledMenuId(this.openActionsMenuId, rowId);
      this.openActionsMenuId = nextId;

      if (!nextId) {
        return;
      }

      const position = computeFloatingMenuPosition(event.currentTarget, 210, 6, 8);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    closeActionsMenu() {
      this.openActionsMenuId = null;
    },
    handleGlobalClick(event) {
      if (!this.openActionsMenuId) {
        return;
      }

      const target = event?.target;
      if (target instanceof Element && target.closest('.app-table-floating-menu')) {
        return;
      }

      if (target instanceof Element && target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.closeActionsMenu();
    },
    emitAction(action, row) {
      this.closeActionsMenu();

      if (action === 'toggle-status') {
        this.emitToggleStatus(row);
        return;
      }

      if (action === 'delete') {
        this.emitDelete(row);
      }
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      const validPage = ensureValidPage(nextPage, this.normalizedPagination.total_pages);

      if (validPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', validPage);
    },
  },
};
</script>

<style scoped>
.customers-table-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.customers-table-sort-btn--right {
  width: 100%;
  justify-content: flex-end;
}

.customers-table-link {
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
}

.customers-table-link:hover {
  text-decoration: underline;
}
</style>
